/**
 * Integrations admin handler
 * Manages available Nango integrations
 */

import { useState, useCallback, useEffect } from "react";
import type { UseAdminAppReturn } from "../useAdminApp";

export interface Integration extends Record<string, unknown> {
  unique_key: string;
  provider: string;
  created_at: string;
  updated_at: string;
}

export interface IntegrationsAdminHandler {
  integrations: Integration[];
  loading: boolean;
  error: string | null;
  loadIntegrations: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useIntegrationsAdmin(adminApp: UseAdminAppReturn): IntegrationsAdminHandler {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadIntegrations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/integrator/integrations`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to load integrations');
      const data = await response.json();
      setIntegrations(data.integrations || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      console.error('Failed to load integrations:', err);
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  // Auto-load on mount and workspace change
  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations, adminApp.workspaceId]);

  return {
    integrations,
    loading,
    error,
    loadIntegrations,
    refresh: loadIntegrations,
  };
}

